import { useState, useEffect } from "react";
import axios from "axios";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2, Cpu, Package, MapPin, Presentation, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Simplified Turkey map TopoJSON URL
const TURKEY_TOPO_JSON = "https://raw.githubusercontent.com/cihadturhan/tr-geojson/master/geo/tr-cities-utf8.json";

const Dashboard = () => {
  const [statistics, setStatistics] = useState({
    total_hospitals: 0,
    total_devices: 0,
    total_probes: 0,
    cities_count: 0,
    active_devices: 0,
  });
  const [biovicsStats, setBiovicsStats] = useState({ total_biovics: 0 });
  const [demoStats, setDemoStats] = useState({
    total_demos: 0,
    converted_to_sale: 0,
    conversion_rate: 0,
    current_month_demos: 0,
  });
  const [cityStats, setCityStats] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [cityHospitals, setCityHospitals] = useState([]);
  const [cityDistricts, setCityDistricts] = useState([]);
  const [cityDemos, setCityDemos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCityDetails, setLoadingCityDetails] = useState(false);

  useEffect(() => {
    fetchStatistics();
    fetchCityStats();
    fetchBiovicsStats();
    fetchDemoStats();
  }, []);

  const fetchBiovicsStats = async () => {
    try {
      const response = await axios.get(`${API}/statistics/biovics`);
      setBiovicsStats(response.data);
    } catch (error) {
      console.error("Error fetching biovics statistics:", error);
    }
  };

  const fetchDemoStats = async () => {
    try {
      const response = await axios.get(`${API}/demos-stats`);
      setDemoStats(response.data);
    } catch (error) {
      console.error("Error fetching demo statistics:", error);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await axios.get(`${API}/statistics`);
      setStatistics(response.data);
    } catch (error) {
      console.error("Error fetching statistics:", error);
      toast.error("İstatistikler yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  const fetchCityStats = async () => {
    try {
      const response = await axios.get(`${API}/statistics/by-city`);
      setCityStats(response.data);
    } catch (error) {
      console.error("Error fetching city stats:", error);
    }
  };

  const fetchCityHospitals = async (cityNames) => {
    setLoadingCityDetails(true);
    try {
      const allHospitals = [];
      const allDistricts = new Set();
      const allDemos = [];
      
      for (const cityName of cityNames) {
        const response = await axios.get(`${API}/hospitals?city=${cityName}`);
        allHospitals.push(...response.data);
        response.data.forEach(h => allDistricts.add(h.district));
        
        // Fetch demos for each hospital in this city
        for (const hospital of response.data) {
          const demosRes = await axios.get(`${API}/demos?hospital_id=${hospital.id}`);
          allDemos.push(...demosRes.data);
        }
      }
      
      setCityHospitals(allHospitals);
      setCityDistricts([...allDistricts].sort());
      setCityDemos(allDemos);
    } catch (error) {
      console.error("Error fetching city hospitals:", error);
      toast.error("Şehir hastaneleri yüklenirken hata oluştu");
    } finally {
      setLoadingCityDetails(false);
    }
  };

  const handleCityClick = (geo) => {
    const cityName = geo.properties.name;
    
    setSelectedCities(prev => {
      const isSelected = prev.some(city => city.city === cityName);
      let newSelection;
      
      if (isSelected) {
        // Remove city
        newSelection = prev.filter(city => city.city !== cityName);
      } else {
        // Add city
        const cityData = cityStats.find((c) => c.city === cityName);
        const newCity = cityData || { city: cityName, hospital_count: 0, device_count: 0, probe_count: 0 };
        newSelection = [...prev, newCity];
      }
      
      // Fetch hospitals for all selected cities
      if (newSelection.length > 0) {
        fetchCityHospitals(newSelection.map(c => c.city));
      } else {
        setCityHospitals([]);
        setCityDistricts([]);
        setCityDemos([]);
      }
      
      return newSelection;
    });
  };

  const statCards = [
    {
      title: "Toplam Hastane",
      value: statistics.total_hospitals,
      icon: Building2,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      testId: "stat-hospitals"
    },
    {
      title: "Toplam Cihaz",
      value: statistics.total_devices,
      icon: Cpu,
      color: "text-green-600",
      bgColor: "bg-green-50",
      testId: "stat-devices"
    },
    {
      title: "Bu Ay Verilen Prob",
      value: statistics.total_probes,
      icon: Package,
      color: "text-purple-600",
      bgColor: "bg-purple-50",
      testId: "stat-probes",
      subtitle: new Date().toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' })
    },
    {
      title: "Toplam Demo",
      value: demoStats.total_demos,
      icon: Presentation,
      color: "text-orange-600",
      bgColor: "bg-orange-50",
      testId: "stat-demos",
      subtitle: `Satışa Dönüşen: ${demoStats.converted_to_sale}`
    },
    {
      title: "Demo Dönüşüm Oranı",
      value: `%${demoStats.conversion_rate}`,
      icon: TrendingUp,
      color: "text-teal-600",
      bgColor: "bg-teal-50",
      testId: "stat-conversion",
      subtitle: `Bu Ay: ${demoStats.current_month_demos} demo`
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="dashboard-loading">
        <p className="text-muted-foreground">Yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight" data-testid="dashboard-title">
          Dashboard
        </h1>
        <p className="text-base text-muted-foreground mt-2">
          Medikal cihaz ve hastane takip sistemi
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <Card key={card.title} className="stat-card" data-testid={card.testId}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">
                      {card.title}
                    </p>
                    <p className="text-2xl font-bold mt-1">{card.value}</p>
                    {card.subtitle && (
                      <p className="text-xs text-muted-foreground mt-1">{card.subtitle}</p>
                    )}
                  </div>
                  <div className={`${card.bgColor} ${card.color} p-3 rounded-xl`}>
                    <Icon className="w-6 h-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Map and City Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2" data-testid="turkey-map-card">
          <CardHeader>
            <CardTitle>Türkiye Haritası</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="map-container" style={{ height: '500px' }}>
              <ComposableMap
                projection="geoMercator"
                projectionConfig={{
                  center: [35, 39],
                  scale: 2200,
                }}
                width={800}
                height={500}
                data-testid="interactive-map"
              >
                <Geographies geography={TURKEY_TOPO_JSON}>
                  {({ geographies }) =>
                    geographies.map((geo) => {
                      const cityData = cityStats.find(
                        (c) => c.city === geo.properties.name
                      );
                      const hasData = cityData && cityData.hospital_count > 0;
                      const isSelected = selectedCities.some(c => c.city === geo.properties.name);

                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          onClick={() => handleCityClick(geo)}
                          className={`city-path ${isSelected ? 'selected' : ''}`}
                          style={{
                            default: {
                              fill: isSelected ? '#A5B4FC' : hasData ? '#C7D2FE' : '#FFFFFF',
                              outline: 'none',
                            },
                            hover: {
                              fill: '#EEF2FF',
                              outline: 'none',
                            },
                            pressed: {
                              fill: '#A5B4FC',
                              outline: 'none',
                            },
                          }}
                          data-testid={`map-city-${geo.properties.name}`}
                        />
                      );
                    })
                  }
                </Geographies>
              </ComposableMap>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="city-details-card">
          <CardHeader>
            <CardTitle>Şehir Detayları</CardTitle>
          </CardHeader>
          <CardContent>
            {selectedCities.length > 0 ? (
              <div className="space-y-4" data-testid="selected-city-info">
                <div>
                  <h3 className="text-2xl font-semibold text-primary">
                    {selectedCities.map(c => c.city).join(", ")}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    {selectedCities.length} şehir seçili
                  </p>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-secondary rounded-lg">
                    <span className="text-sm font-medium">Hastane Sayısı</span>
                    <span className="text-lg font-bold">
                      {selectedCities.reduce((sum, c) => sum + c.hospital_count, 0)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-secondary rounded-lg">
                    <span className="text-sm font-medium">Cihaz Sayısı</span>
                    <span className="text-lg font-bold">
                      {selectedCities.reduce((sum, c) => sum + c.device_count, 0)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-secondary rounded-lg">
                    <span className="text-sm font-medium">Prob Sayısı</span>
                    <span className="text-lg font-bold">
                      {selectedCities.reduce((sum, c) => sum + c.probe_count, 0)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="flex items-center gap-2">
                      <Presentation className="w-4 h-4 text-orange-600" />
                      <span className="text-sm font-medium text-orange-700">Demo Sayısı</span>
                    </div>
                    <div className="text-right">
                      <span className="text-lg font-bold text-orange-600">
                        {cityDemos.length}
                      </span>
                      {cityDemos.filter(d => d.converted_to_sale).length > 0 && (
                        <p className="text-xs text-green-600">
                          {cityDemos.filter(d => d.converted_to_sale).length} satışa dönüştü
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {loadingCityDetails ? (
                  <div className="text-center py-4">
                    <p className="text-sm text-muted-foreground">Yükleniyor...</p>
                  </div>
                ) : (
                  <>
                    {/* Districts */}
                    {cityDistricts.length > 0 && (
                      <div className="pt-4 border-t border-border">
                        <h4 className="text-sm font-semibold mb-2">İlçeler ({cityDistricts.length})</h4>
                        <div className="max-h-32 overflow-y-auto space-y-1">
                          {cityDistricts.map((district) => (
                            <div
                              key={district}
                              className="text-sm px-2 py-1 bg-accent rounded text-accent-foreground"
                            >
                              {district}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Hospitals */}
                    {cityHospitals.length > 0 && (
                      <div className="pt-4 border-t border-border">
                        <h4 className="text-sm font-semibold mb-2">Hastaneler ({cityHospitals.length})</h4>
                        <div className="max-h-48 overflow-y-auto space-y-2">
                          {cityHospitals.map((hospital) => (
                            <div
                              key={hospital.id}
                              className="p-3 bg-accent rounded-lg"
                              data-testid={`city-hospital-${hospital.id}`}
                            >
                              <p className="font-medium text-sm">{hospital.name}</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {hospital.city} - {hospital.district}
                              </p>
                              {hospital.contact_person && (
                                <p className="text-xs text-muted-foreground">
                                  İletişim: {hospital.contact_person}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="text-center py-12" data-testid="no-city-selected">
                <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  Haritadan şehir seçin
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Birden fazla şehir seçebilirsiniz
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Cities */}
      <Card data-testid="top-cities-card">
        <CardHeader>
          <CardTitle>En Çok Hastane Bulunan Şehirler</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {cityStats.slice(0, 5).map((city, index) => (
              <div
                key={city.city}
                className="flex items-center justify-between p-4 bg-secondary rounded-lg table-row"
                data-testid={`top-city-${index}`}
              >
                <div className="flex items-center gap-4">
                  <div className="bg-primary text-primary-foreground w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm">
                    {index + 1}
                  </div>
                  <span className="font-medium">{city.city}</span>
                </div>
                <div className="flex gap-6 text-sm">
                  <span className="text-muted-foreground">
                    <span className="font-semibold text-foreground">{city.hospital_count}</span> Hastane
                  </span>
                  <span className="text-muted-foreground">
                    <span className="font-semibold text-foreground">{city.device_count}</span> Cihaz
                  </span>
                  <span className="text-muted-foreground">
                    <span className="font-semibold text-foreground">{city.probe_count}</span> Prob
                  </span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;